class LectronProductForm extends HTMLElement {
  connectedCallback() {
    this.productForm = document.getElementById(this.dataset.formId);
    if (!this.productForm) return;

    this.variantInput = this.productForm.querySelector('input[name="id"]');
    if (!this.variantInput) return;

    this.variants = this._loadVariants();
    if (!this.variants.length) return;

    this.sizeMap = this._buildSizeMap();
    this.allowedSizes = (this.dataset.allowedSizes || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    this.anodizeToggle = this.querySelector('[data-lectron-anodize-toggle]');
    this.colorWrap = this.querySelector('[data-lectron-color-wrap]');
    this.colorInputs = this.querySelectorAll('[data-lectron-color]');
    this.sizeInputs = this.querySelectorAll('[data-lectron-size]');
    this.errorEl = this.querySelector('[data-lectron-error]');

    this._wireEvents();
    this._suppressNativeVariantPicker();
    this._syncVariant();
  }

  _loadVariants() {
    const node = this.querySelector('[data-lectron-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      console.warn('[lectron-product-form] failed to parse variants', e);
      return [];
    }
  }

  // Build { "25mm": { raw: id, anodized: id }, "29mm": { ... } } from variant titles
  _buildSizeMap() {
    const map = {};
    const sizeRe = /(\d+(?:\.\d+)?)\s*mm/i;
    for (const v of this.variants) {
      const titleAfterPipe = v.title.includes('|') ? v.title.split('|').pop() : v.title;
      const match = sizeRe.exec(titleAfterPipe);
      if (!match) continue;
      const sizeKey = `${match[1]}mm`;
      const isAnodized = /anodi[sz]ed/i.test(v.title);
      if (!map[sizeKey]) map[sizeKey] = { raw: null, anodized: null };
      if (isAnodized) map[sizeKey].anodized = v.id;
      else map[sizeKey].raw = v.id;
    }
    return map;
  }

  _wireEvents() {
    this.sizeInputs.forEach((el) =>
      el.addEventListener('change', () => this._syncVariant())
    );
    if (this.anodizeToggle) {
      this.anodizeToggle.addEventListener('change', () => this._onAnodizeToggle());
    }
    this.productForm.addEventListener('submit', (e) => this._onSubmit(e));
  }

  _onAnodizeToggle() {
    const on = this.anodizeToggle.checked;
    if (this.colorWrap) {
      if (on) this.colorWrap.removeAttribute('hidden');
      else this.colorWrap.setAttribute('hidden', '');
    }
    this.colorInputs.forEach((inp) => {
      inp.disabled = !on;
      if (!on) inp.checked = false;
    });
    this._syncVariant();
  }

  _getSelectedSize() {
    const sel = Array.from(this.sizeInputs).find((i) => i.checked);
    return sel ? sel.value : this.allowedSizes[0] || null;
  }

  _syncVariant() {
    const size = this._getSelectedSize();
    if (!size || !this.sizeMap[size]) return;
    const anodized = this.anodizeToggle && this.anodizeToggle.checked;
    const targetId =
      anodized && this.sizeMap[size].anodized
        ? this.sizeMap[size].anodized
        : this.sizeMap[size].raw;

    if (targetId && this.variantInput.value !== String(targetId)) {
      this.variantInput.value = targetId;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      this._publishVariantUpdate(targetId);
    }
  }

  // Re-fetch the rendered section for the new variant and dispatch Horizon's
  // 'variant:update' event so the price block, inventory, gallery, etc. update.
  async _publishVariantUpdate(variantId) {
    const sectionId = this._getSectionId();
    if (!sectionId) return;

    const variant = this.variants.find((v) => v.id === variantId);
    if (!variant) return;

    const url = `${window.location.pathname}?section_id=${sectionId}&variant=${variantId}`;
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) return;
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      const section = this.closest('.shopify-section') || document.querySelector('.shopify-section');
      if (!section) return;

      const event = new CustomEvent('variant:update', {
        bubbles: true,
        detail: {
          resource: variant,
          sourceId: this.id,
          data: {
            html,
            productId: section.dataset.productId || '',
          },
        },
      });
      section.dispatchEvent(event);
    } catch (e) {
      console.warn('[lectron-product-form] variant update fetch failed', e);
    }
  }

  _getSectionId() {
    const formId = this.dataset.formId;
    if (!formId) return null;
    return formId.replace('BuyButtons-ProductForm-', '');
  }

  _suppressNativeVariantPicker() {
    const native = document.querySelector('variant-selects, .variant-picker, [is="variant-selects"]');
    if (native) native.style.display = 'none';
  }

  _onSubmit(e) {
    const errors = [];
    const size = this._getSelectedSize();
    if (!size) errors.push('Please select a size.');

    if (this.anodizeToggle && this.anodizeToggle.checked) {
      const colorPicked = Array.from(this.colorInputs).some((i) => i.checked);
      if (!colorPicked) errors.push('Please pick an anodize color.');
    }

    if (errors.length && this.errorEl) {
      this.errorEl.textContent = errors.join(' ');
      this.errorEl.removeAttribute('hidden');
      e.preventDefault();
      e.stopImmediatePropagation();
      return false;
    }

    if (this.errorEl) {
      this.errorEl.textContent = '';
      this.errorEl.setAttribute('hidden', '');
    }

    // Mirror selections into cart note + attributes so the Flow email picks
    // them up. Fire-and-forget: don't block Horizon's AJAX add-to-cart,
    // don't re-submit the form (that was causing doubled qty).
    if (!this._cartContextWritten) {
      this._cartContextWritten = true;
      this._writeCartContext();
    }
  }

  _collectOrderInfo() {
    const product = (document.querySelector('h1') || {}).textContent || '';
    const ymm = this.querySelector('[data-lectron-ymm]')?.value || '';
    const cc = this.querySelector('[data-lectron-cc]')?.value || '';
    const notes = this.querySelector('textarea[name="properties[Notes]"]')?.value || '';
    const size = this._getSelectedSize() || '';
    const color = Array.from(this.colorInputs).find((i) => i.checked)?.value || '';
    const anodized = this.anodizeToggle && this.anodizeToggle.checked;
    return { product: product.trim(), ymm, cc, size, color, anodized, notes };
  }

  async _writeCartContext() {
    const info = this._collectOrderInfo();
    const lines = [
      info.product && `Product: ${info.product}`,
      info.size && `Size: ${info.size}`,
      info.ymm && `Year, Make, Model: ${info.ymm}`,
      info.cc && `Engine CC: ${info.cc}`,
      info.anodized && info.color && `Anodized Color: ${info.color}`,
      info.notes && `Customer Notes: ${info.notes}`,
    ].filter(Boolean);
    const note = lines.join('\n');

    const attributes = {};
    if (info.ymm) attributes['Year, Make, Model'] = info.ymm;
    if (info.cc) attributes['Engine CC'] = info.cc;
    if (info.anodized && info.color) attributes['Anodized Color'] = info.color;
    if (info.notes) attributes['Customer Notes'] = info.notes;

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note, attributes }),
      });
    } catch (err) {
      console.warn('[lectron-product-form] cart context write failed', err);
    }
  }
}

if (!customElements.get('lectron-product-form')) {
  customElements.define('lectron-product-form', LectronProductForm);
}
